import { useDebugValue, useEffect } from 'react';
import { ApiRequest } from '@zeal/zeal-lib';

import { useLoggedIn } from '../ACL/useLogin';
import { useAppStore } from '../store/appStore';
import { MerchantType, useAuthStore } from '@app/store/authStore';
import { ENDPOINTS } from '@app/Services/endpoints';
import { useQuery } from '@tanstack/react-query';

export type AccountInfoParams = {
	id?: string;
	name?: string;
	hasOwnApp?: string;
};
export type AccountInfo = {
	appId: string;
	appName: string | null;
	hasOwnApp: boolean;
	isAppOwn: boolean;
};

async function apiBusinessId() {
	return new ApiRequest('me/app')
		.addHeader('Accept', 'application/json')
		.GET<{
			appId: string;
			hasWhitelabel: boolean;
		}>()
		.then((data) => {
			data.appId = data.appId?.toString();

			return data;
		});
}

export default function useBusinessId() {
	const { isLoggedIn } = useLoggedIn();

	const q = useQuery({
		queryFn: apiBusinessId,
		staleTime: Infinity,
		queryKey: ['z-account-id'],
		enabled: isLoggedIn && !!useAuthStore.getState().loginVault?.id,
	});

	useDebugValue(q);

	return q;
}

export type BusinessInfoAPI = {
	uuid: string;
	name: string;
	email: string;
	created_at: string;
	all_branches: unknown;
	is_super_admin: boolean;
	credit: number;
	business: {
		uuid: string;
		name: string;
		photo: string;
		cover: string;
		country: string;
		settings: {
			booking: boolean;
			booking_cancellation_fees: number;
			can_upload_receipt: boolean;
			currency: string;
			delete_card_enabled: boolean;
			gift_feature_enabled: boolean;
			has_branch_grouping: boolean;
			has_menu: boolean;
			has_only_one_menu: boolean;
			locale: string;
			loyalty: boolean;
			menu_item_availability_config_enabled: boolean;
			no_show_protection_fees: number;
			offline_punch_enabled: boolean;
			order_accepting_status_by: string | null;
			ordering: boolean;
			payment: boolean;
			payment_link_enabled: boolean;
			pos_integration_enabled: boolean;
			qr_payment_enabled: boolean;
			slot_duration_minutes: number;
			has_branch_availability_per_order_type: boolean;
			tap_to_pay_enabled: boolean;
		};
		terminal_settings: {
			feedback_enabled: boolean;
			card_triggered_enabled: boolean;
			micro_loyalty_enabled: boolean;
		};
		has_scheduled_call_for_loyalty_link?: boolean;
	};
	type: MerchantType;
};

async function apiBusinessInfo(): Promise<BusinessInfoAPI> {
	return new ApiRequest(ENDPOINTS.business.info)
		.addHeader('Accept', 'application/json')
		.GET<{ data: BusinessInfoAPI }>()
		.then((res) => {
			const data: BusinessInfoAPI = res?.data || {};

			return data;
		});
}

export function useBusinessInfo() {
	const { isLoggedIn } = useAuthStore((store) => store);

	const q = useQuery({
		queryFn: apiBusinessInfo,
		staleTime: Infinity,
		queryKey: ['z-account-info'],
		enabled: isLoggedIn,
	});

	useDebugValue(q);

	return { ...q };
}

export function getAccountInfo(): AccountInfo {
	const { appState } = useAppStore.getState();

	return {
		appId: (appState?.currentAppId ?? 1)?.toString(),
		appName: appState?.currentAppName ?? 'zeal',
		hasOwnApp: appState?.hasCustomApp ?? false,
		isAppOwn: appState?.isCustomAppActive ?? false,
	};
}

export function useAccountInfo() {
	const { data: customAppData, isLoading: isBusinessIdLoading } =
		useBusinessId();
	const { data: businessInfo, isLoading: isBusinessInfoLoading } =
		useBusinessInfo();

	const { updateAppState, appState, activateCustomApp } = useAppStore(
		(state) => state
	);

	const updateAccountInfo = (info: { appId: string; appName: string }) => {
		updateAppState({
			isCustomAppActive: info.appId !== '1',
			currentAppName: info.appName,
			currentAppId: info.appId,
		});
	};

	// Handle custom app data when it becomes available
	useEffect(() => {
		if (customAppData) {
			const hasWhitelabel = !!customAppData?.hasWhitelabel;

			updateAppState({
				hasCustomApp: hasWhitelabel,
				customAppId: customAppData?.appId,
			});
		}
	}, [customAppData, updateAppState]);

	// Handle business info and auto-activation logic
	useEffect(() => {
		if (businessInfo && customAppData) {
			const businessName = businessInfo?.business?.name;
			const hasWhitelabel = !!customAppData?.hasWhitelabel;
			const customAppId = customAppData?.appId;

			// Update custom app name
			updateAppState({
				customAppName: businessName,
			});

			// Auto-activate custom app on first load if business has one
			if (!appState?.isSwitchedOnLoad && hasWhitelabel && customAppId) {
				console.log('Auto-activating custom app on first load:', {
					appId: customAppId,
					appName: businessName,
				});

				activateCustomApp({
					appId: customAppId?.toString(),
					appName: businessName,
				});
			} else if (!appState?.isSwitchedOnLoad) {
				// Set default to Zeal if no custom app
				updateAppState({
					isCustomAppActive: false,
					currentAppId: '1',
					currentAppName: 'zeal',
					isSwitchedOnLoad: true,
				});
			}
		}
	}, [
		businessInfo,
		customAppData,
		appState?.isSwitchedOnLoad,
		updateAppState,
		activateCustomApp,
	]);

	return {
		updateApp: updateAccountInfo,
		isLoading: isBusinessIdLoading || isBusinessInfoLoading,
	};
}

const REFETCH_PERMISSIONS_EVENT = 'refetch-permissions-event';

export function refetchUserPermissionsEvent() {
	return {
		emit() {
			const evt = new CustomEvent(REFETCH_PERMISSIONS_EVENT, {
				detail: { status: 403 },
			});
			window.dispatchEvent(evt);
		},
		on(handler: EventListenerOrEventListenerObject) {
			window.addEventListener(REFETCH_PERMISSIONS_EVENT, handler);

			return () => {
				window.removeEventListener(REFETCH_PERMISSIONS_EVENT, handler);
			};
		},
	};
}

export interface BusinessProperties {
	booking: boolean;
	payment: boolean;
	ordering: boolean;
	loyalty: boolean;
	gift_feature: boolean;
	business_day_payment_reports: boolean;
	business_day_branch_payment_reports: boolean;
	team: boolean;
	push_notification: boolean;
	currency: string;
	order_acceptance_status_by: string;
	locale: string;
}

async function apiBusinessProperties() {
	return new ApiRequest('v3/business/properties')
		.addHeader('Accept', 'application/json')
		.GET<ApiResponse<BusinessProperties>>()
		.then(({ data }) => data);
}

export function useBusinessProperties() {
	const { isLoggedIn } = useLoggedIn();

	const q = useQuery({
		queryFn: apiBusinessProperties,
		staleTime: Infinity,
		queryKey: ['z-business-properties'],
		enabled: isLoggedIn && !!useAuthStore.getState().loginVault?.id,
	});

	useDebugValue(q);

	return q;
}
